import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import PendingApproval from './pages/PendingApproval';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import Areas from './pages/Areas';
import Records from './pages/Records';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import Companies from './pages/Companies';
import UserManagement from './pages/UserManagement';
import './App.css';

const ProtectedRoute = ({ children, requireMaster = false, requireAdmin = false }) => {
    const { isAuthenticated, loading, user, isMaster, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check if user is pending approval
    if (!user?.is_active && user?.role !== 'master') {
        return <Navigate to="/pending-approval" replace />;
    }

    // Master-only routes
    if (requireMaster && !isMaster) {
        return <Navigate to="/dashboard" replace />;
    }

    // Admin-only routes
    if (requireAdmin && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // Master should go to companies page, not dashboard
    if (isMaster && window.location.pathname === '/dashboard') {
        return <Navigate to="/companies" replace />;
    }

    return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user, isMaster } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (isAuthenticated) {
        // Check if user is pending
        if (!user?.is_active && user?.role !== 'master') {
            return <Navigate to="/pending-approval" replace />;
        }
        // Redirect based on role
        if (isMaster) {
            return <Navigate to="/companies" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                }
            />
            <Route path="/pending-approval" element={<PendingApproval />} />

            {/* Master-only Routes */}
            <Route
                path="/companies"
                element={
                    <ProtectedRoute requireMaster>
                        <Companies />
                    </ProtectedRoute>
                }
            />

            {/* Admin Routes */}
            <Route
                path="/users"
                element={
                    <ProtectedRoute requireAdmin>
                        <UserManagement />
                    </ProtectedRoute>
                }
            />

            {/* Protected Routes (regular users) */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/devices"
                element={
                    <ProtectedRoute>
                        <Devices />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/areas"
                element={
                    <ProtectedRoute>
                        <Areas />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/records"
                element={
                    <ProtectedRoute>
                        <Records />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/alerts"
                element={
                    <ProtectedRoute>
                        <Alerts />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                }
            />

            {/* Redirect root based on role */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404 - Redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <WebSocketProvider>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                </WebSocketProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
